'use client';
import { type RefObject, useCallback, useMemo, useState } from 'react';

import { useRTL } from './useRTL';

type Position = {
	x: number;
	y: number;
};

type DropdownVerticalPosition = 'bottom' | 'middle';
type DropdownHorizontalPosition = 'right' | 'start';
type DropdownVerticalAlignment = 'above' | 'below';
type DropdownHorizontalAlignment = 'left' | 'right';

export interface UseDropdownConfiguration {
	forceHorizontalOrientation?: DropdownHorizontalAlignment | null;
	forceVerticalOrientation?: DropdownVerticalAlignment | null;
	horizontalAlignment?: DropdownHorizontalPosition;
	negativeHorizontalPadding?: number;
	verticalAlignment?: DropdownVerticalPosition;
}

type GetDropdownPosition = {
	activatorHeight: number;
	activatorWidth: number;
	configuration: Required<UseDropdownConfiguration>;
	dropdownRect: DOMRect;
	isRTL: boolean;
	windowHeight: number;
	windowWidth: number;
};

type GetHorizontalPositionParams = Pick<
	GetDropdownPosition,
	'activatorWidth' | 'configuration' | 'dropdownRect' | 'isRTL' | 'windowWidth'
>;

const defaultConfiguraiton: Required<UseDropdownConfiguration> = {
	forceHorizontalOrientation: null,
	forceVerticalOrientation: null,
	horizontalAlignment: 'start',
	negativeHorizontalPadding: 0,
	verticalAlignment: 'middle'
};

const DROPDOWN_HORIZONTAL_PADDING = 40;

const getHorizontalPosition = ({
	activatorWidth,
	configuration,
	dropdownRect,
	isRTL,
	windowWidth
}: GetHorizontalPositionParams) => {
	const alignmentPadding = configuration.horizontalAlignment === 'right' ? activatorWidth : 0;
	const alignmentPaddingReserve = configuration.horizontalAlignment === 'right' ? 0 : activatorWidth;
	const isForcedToRight =
		(isRTL && configuration.forceHorizontalOrientation === 'left') ||
		configuration.forceHorizontalOrientation === 'right';
	const isForcedToLeft =
		(isRTL && configuration.forceHorizontalOrientation === 'right') ||
		configuration.forceHorizontalOrientation === 'left';
	const showRegularOrientation = isRTL
		? dropdownRect.left - alignmentPadding > 0 || configuration.forceHorizontalOrientation === 'right'
		: dropdownRect.left + dropdownRect.width + alignmentPadding < windowWidth;

	return (showRegularOrientation && !isForcedToLeft) || isForcedToRight
		? alignmentPadding - configuration.negativeHorizontalPadding
		: configuration.negativeHorizontalPadding - dropdownRect.width + alignmentPaddingReserve;
};

const getDropdownPosition = ({
	activatorHeight,
	activatorWidth,
	configuration,
	dropdownRect,
	isRTL,
	windowHeight,
	windowWidth
}: GetDropdownPosition) => {
	const x = getHorizontalPosition({
		activatorWidth,
		configuration,
		dropdownRect,
		isRTL,
		windowWidth
	});

	const verticalAlignment = configuration.verticalAlignment === 'bottom' ? activatorHeight : activatorHeight / 2;
	const isModalDisplayedBelow =
		dropdownRect.top + dropdownRect.height + verticalAlignment + DROPDOWN_HORIZONTAL_PADDING < windowHeight;
	const abovePosition =
		configuration.verticalAlignment === 'middle'
			? 0 - dropdownRect.height + verticalAlignment
			: 0 - dropdownRect.height;
	const y =
		(configuration.forceVerticalOrientation !== 'above' && isModalDisplayedBelow) ||
		configuration.forceVerticalOrientation === 'below'
			? verticalAlignment
			: abovePosition;

	return { x, y };
};

/**
 * Handles the opening and closing of a dropdown and calculates its position in the screen
 * @param references.activator Activator element reference.
 * @param references.dropdown Dropdown element reference.
 * @param config.horizontalAlignment Horizontal alignment. It can be 'start' or 'right'. Default is 'start'.
 * @param config.verticalAlignment Vertical alignment. It can be 'middle' or 'bottom'. Default is 'middle'.
 * @param config.forceHorizontalOrientation Forces horizontal orientation. For RTL it will revert the orientation. Default is null.
 * @param config.forceVerticalOrientation Forces vertical orientation. Default is null.
 * @param config.negativeHorizontalPadding Negative horizontal padding to render the dropdown inside of the activator. Default is 0.
 */
export const useDropdown = (
	{ activator, dropdown }: { activator: RefObject<HTMLElement>; dropdown: RefObject<HTMLElement> },
	config: UseDropdownConfiguration = {}
) => {
	const { isRTL } = useRTL();
	const [isDropdownVisible, setDropdownVisibiliy] = useState<boolean>(false);
	const [dropdownPosition, setDropdownPosition] = useState<Position>({ x: 0, y: 0 });
	const [dropdownOpacity, setDropdownOpacity] = useState<number>(0);
	const configuration = useMemo(() => {
		return { ...defaultConfiguraiton, ...config };
	}, [config]);

	const openDropdown = useCallback(() => {
		if (isDropdownVisible) return;
		setDropdownVisibiliy(true);

		setTimeout(() => {
			if (!activator.current || !dropdown.current) return;

			const { x, y } = getDropdownPosition({
				activatorHeight: activator.current.offsetHeight || 0,
				activatorWidth: activator.current.offsetWidth || 0,
				configuration,
				dropdownRect: dropdown.current.getBoundingClientRect(),
				isRTL,
				windowHeight: document.body.offsetHeight,
				windowWidth: document.body.offsetWidth
			});

			setDropdownPosition({ x, y });
			setDropdownOpacity(1);
		}, 50);
	}, [dropdown, activator, isDropdownVisible, isRTL, configuration]);

	const hideDropdown = useCallback((ev?: Event) => {
		if (ev) {
			const disabledAttribute = (ev?.target as HTMLElement).attributes.getNamedItem('data-disabled');

			if (disabledAttribute?.value === 'true') return;
		}

		setDropdownVisibiliy(false);
		setDropdownOpacity(0);
		setDropdownPosition({ x: 0, y: 0 });
	}, []);

	const recalculatePosition = useCallback(() => {
		if (!activator.current || !dropdown.current || dropdownPosition.y >= 0) return;

		const dropdownRect = dropdown.current.getBoundingClientRect();
		const activatorHeight = activator.current.offsetHeight || 0;
		const verticalAlignment = configuration.verticalAlignment === 'bottom' ? activatorHeight : activatorHeight / 2;
		const abovePosition =
			configuration.verticalAlignment === 'middle'
				? 0 - dropdownRect.height + verticalAlignment
				: 0 - dropdownRect.height;

		setDropdownPosition(value => ({
			x: value.x,
			y: abovePosition
		}));
	}, [dropdown, activator, configuration, dropdownPosition.y]);

	return {
		dropdownOpacity,
		dropdownPosition,
		hideDropdown,
		isDropdownVisible,
		openDropdown,
		recalculatePosition
	};
};
